import { datadogRum } from '@datadog/browser-rum';

import { isPilotTransportOn, pilotApiOrigin } from '@/lib/pilot-api';
import { redactSensitiveStringsDeep } from '@/lib/telemetry-redaction';

import type { RumBeforeSend } from '@datadog/browser-rum';

/**
 * Datadog RUM client init + thin wrappers for `addAction` / `addTiming`.
 *
 * Boot lives next to `initSentry()` in `main.tsx`. RUM stays a no-op when
 * `VITE_DATADOG_RUM_CLIENT_TOKEN` is absent — local dev and CI never ship
 * telemetry without explicit opt-in, matching the Sentry pattern.
 *
 * PII discipline: this module MUST NOT receive raw search strings, email
 * addresses, subject lines, names, or folder labels. Callers send opaque IDs,
 * enums, counts, and booleans only. Session replay masks all rendered text and
 * user input so constituent content cannot be reconstructed from the DOM.
 */

let initialized = false;

interface RumEnv {
  readonly clientToken?: string;
  readonly applicationId?: string;
  readonly site?: string;
  readonly service?: string;
  readonly env?: string;
  readonly sessionSampleRate?: string;
  readonly sessionReplaySampleRate?: string;
  readonly version?: string;
}

// A dotenv file declares an unset key as `KEY=`, which Vite exposes as '' rather
// than undefined, so blank has to mean unset for the `??` defaults to apply.
function blankToUndefined(value: string | undefined): string | undefined {
  return value === '' ? undefined : value;
}

function readEnv(): RumEnv {
  const env = import.meta.env as Record<string, string | undefined>;
  return {
    clientToken: blankToUndefined(env.VITE_DATADOG_RUM_CLIENT_TOKEN),
    applicationId: blankToUndefined(env.VITE_DATADOG_RUM_APPLICATION_ID),
    site: blankToUndefined(env.VITE_DATADOG_RUM_SITE),
    service: blankToUndefined(env.VITE_DATADOG_RUM_SERVICE),
    env: blankToUndefined(env.VITE_DATADOG_RUM_ENV),
    sessionSampleRate: blankToUndefined(env.VITE_DATADOG_RUM_SESSION_SAMPLE_RATE),
    sessionReplaySampleRate: blankToUndefined(env.VITE_DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE),
    version: blankToUndefined(env.VITE_APP_VERSION),
  };
}

/** Datadog records view/resource URLs independently of application logging. */
export const redactDatadogRumEvent: RumBeforeSend = (event) => {
  // Preserve Datadog's top-level schema keys (notably its structured `error`
  // object) while applying key-aware redaction throughout every field value.
  redactSensitiveStringsDeep(event);
  return true;
};

/** True when a request URL's origin is exactly the pilot API origin. */
export function isPilotOrigin(url: string): boolean {
  if (pilotApiOrigin === null) return false;
  try {
    return new URL(url).origin === pilotApiOrigin;
  } catch {
    return false;
  }
}

export function initDatadogRum(): void {
  if (initialized) {
    return;
  }

  const env = readEnv();
  if (!env.clientToken || !env.applicationId) {
    console.warn(
      'Datadog RUM client token or application ID not configured. Skipping RUM initialization.',
    );
    return;
  }

  datadogRum.init({
    applicationId: env.applicationId,
    clientToken: env.clientToken,
    site: env.site ?? 'ap2.datadoghq.com',
    service: env.service ?? 'public-pulse-web',
    env: env.env ?? import.meta.env.MODE,
    version: env.version,
    sessionSampleRate: Number.parseFloat(env.sessionSampleRate ?? '25'),
    sessionReplaySampleRate: Number.parseFloat(env.sessionReplaySampleRate ?? '0'),
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    defaultPrivacyLevel: 'mask',
    beforeSend: redactDatadogRumEvent,
    // Trace headers go only to the exact pilot origin: they make a cross-origin
    // request preflighted, and no other origin is configured to accept them.
    // A string entry would be a URL-prefix match, which a look-alike host such
    // as `<origin>.attacker.example` also satisfies, so the origin is compared.
    // `propagateTraceBaggage` is on unless set to false in RUM 7.1.0 (its docs
    // say the default is false); left on, it sends session, user and account
    // IDs in a `baggage` header the pilot's CORS allowlist does not accept.
    ...(pilotApiOrigin === null
      ? {}
      : { allowedTracingUrls: [isPilotOrigin], propagateTraceBaggage: false }),
  });

  // The native shell serves this same bundle from a WKWebView whose user agent is
  // mobile Safari's, so RUM cannot tell a TestFlight session from an iPhone
  // browser visit without being told. `VITE_NATIVE_SHELL` is the compile-time
  // define (see vite.config.ts) that tells it. Both shells are tagged, never just
  // the native one, so `@context.app.shell` partitions every session.
  datadogRum.setGlobalContextProperty(
    'app.shell',
    import.meta.env.VITE_NATIVE_SHELL === '1' ? 'ios-native' : 'web',
  );
  datadogRum.setGlobalContextProperty(
    'app.list_transport',
    isPilotTransportOn ? 'core-api' : 'postgrest',
  );

  initialized = true;
}

/** True after a successful `datadogRum.init()` call. */
export function isRumInitialized(): boolean {
  return initialized;
}

/**
 * Record a custom user action. No-op when RUM isn't initialized — callers
 * don't need to gate at the call site.
 *
 * MUST pass IDs / counts / booleans only. Never raw user-typed strings.
 */
export function addRumAction(name: string, context?: Readonly<Record<string, unknown>>): void {
  if (!initialized) {
    return;
  }
  datadogRum.addAction(name, context);
}

/**
 * Record a custom timing (ms since the current RUM view started). No-op
 * when RUM isn't initialized.
 */
export function addRumTiming(name: string, time?: number): void {
  if (!initialized) {
    return;
  }
  datadogRum.addTiming(name, time);
}

/** Test-only reset. Resets the module-local `initialized` flag. */
export function __resetDatadogRumForTests(): void {
  initialized = false;
}
